// Branch Transfer as the mock-up draws it: a right-docked drawer instead of a
// centred popup, with the users to move as chips and a preview of what will
// change before you commit.
//
// The payload is unchanged — the same BranchMapper (EntityID 6, EntityType
// "User", the guids, the signed-in branch as source) goes to the same
// branchTransfer endpoint, and users who already work in the destination are
// still left out, exactly as the old "Add Selected" button did.
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import { fetchUser, fetchUserByBranch } from "../../../redux/userServices";
import { getUserbyBranch } from "../../../services/CoreApiServices";
import { branchTransfer } from "../../../services/AdminApiServices";
import { BranchMapper } from "../../../core/json/BranchMapper";
import { AxDrawer } from "../ui/AdminParts";
import {
  AxAvatar,
  AxCaret,
  AxClearIcon,
  AxSearchIcon,
  AxSendIcon,
  AxWarnIcon,
} from "../ui/adminUi";

const UserBranchTransferDrawer = ({ open, onClose, preselected = [] }) => {
  const dispatch = useDispatch();
  const [chosen, setChosen] = useState([]); // guids
  const [desBranch, setdesBranch] = useState("");
  const [desUsers, setdesUsers] = useState([]);
  const [q, setQ] = useState("");
  const [saving, setSaving] = useState(false);

  const { branch } = useSelector((state) => state.branchlist);
  const { BranchByUser } = useSelector((state) => state.userBranch);
  const { UserList } = useSelector((state) => state.userservice);
  const { userId } = useSelector((state) => state.userauth);

  useEffect(() => {
    dispatch(fetchUser());
  }, []);

  useEffect(() => {
    if (!open) return;
    setChosen(preselected);
    setdesBranch("");
    setdesUsers([]);
    setQ("");
  }, [open]);

  const handleDestination = async (e) => {
    const id = e.target.value;
    setdesBranch(id);
    if (!id) {
      setdesUsers([]);
      return;
    }
    try {
      const response = await getUserbyBranch({ id });
      setdesUsers(response || []);
    } catch (error) {
      console.error("Error fetching the destination branch users:", error);
      setdesUsers([]);
    }
  };

  const alreadyThere = useMemo(() => {
    const set = {};
    (desUsers || []).forEach((u) => {
      set[u.guid] = true;
    });
    return set;
  }, [desUsers]);

  const chosenUsers = useMemo(
    () => (UserList || []).filter((u) => chosen.includes(u.guid)),
    [UserList, chosen]
  );
  const moving = chosenUsers.filter((u) => !alreadyThere[u.guid]);
  const skipped = chosenUsers.length - moving.length;

  const listed = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return UserList || [];
    return (UserList || []).filter((u) => u.name?.toLowerCase().includes(needle));
  }, [UserList, q]);

  const toggle = (guid) =>
    setChosen((c) => (c.includes(guid) ? c.filter((g) => g !== guid) : [...c, guid]));

  const handleBranchTranfer = async () => {
    const destinationBranch = BranchByUser?.find((e) => e.branch?.guid == desBranch)?.branch;
    if (!destinationBranch) {
      toast.error("please select destination");
      return;
    }
    const mapperobj = new BranchMapper();
    mapperobj.EntityID = 6;
    mapperobj.EntityType = "User";
    mapperobj.SelectedEntities = moving.map((e) => e.guid);
    mapperobj.SourceBranch = branch;
    mapperobj.DestinationBranch = destinationBranch;
    mapperobj.updatedUser = `{${userId?.id}}`;
    mapperobj.createdUser = `{${userId?.id}}`;
    setSaving(true);
    try {
      const response = await branchTransfer(mapperobj);
      if (response == true) {
        toast.success("success");
        dispatch(fetchUserByBranch(branch.guid));
        onClose();
      } else if (response == false) {
        toast.error("false");
      }
    } catch (error) {
      console.error("Error transferring the users:", error);
      toast.error(error?.response?.data?.Message || "Branch transfer failed");
    } finally {
      setSaving(false);
    }
  };

  const destName = BranchByUser?.find((e) => e.branch?.guid == desBranch)?.branch?.name;

  return (
    <AxDrawer
      open={open}
      onClose={onClose}
      icon={<AxSendIcon />}
      title="Branch Transfer"
      subtitle={
        chosenUsers.length
          ? chosenUsers.length +
            " user" +
            (chosenUsers.length === 1 ? "" : "s") +
            " selected" +
            (skipped ? " · " + skipped + " already there" : "")
          : "Tick the people you want to move"
      }
      footer={
        <>
          <button type="button" className="axa-btn axa-btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="axa-btn axa-btn-primary"
            disabled={!desBranch || !moving.length || saving}
            onClick={handleBranchTranfer}
          >
            {saving ? "Transferring…" : "Transfer"}
          </button>
        </>
      }
    >
      <div className="axa-secthead" style={{ marginTop: 0 }}>
        Users being transferred
      </div>
      {chosenUsers.length ? (
        <div className="axa-picked">
          {chosenUsers.map((u) => (
            <span className="axa-pchip" key={u.guid}>
              <AxAvatar name={u.name} />
              {u.name}
              <button type="button" aria-label={"Remove " + u.name} onClick={() => toggle(u.guid)}>
                <AxClearIcon />
              </button>
            </span>
          ))}
        </div>
      ) : (
        <div className="axa-emptyline">Nobody picked yet.</div>
      )}

      <div className="axa-secthead">Pick from every user</div>
      <div className="axa-searchwrap" style={{ marginBottom: 8 }}>
        <AxSearchIcon />
        <input
          type="text"
          className="axa-search"
          style={{ paddingRight: 12 }}
          placeholder="Find a person…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </div>
      <div className="axa-pickbox">
        {listed.map((u) => (
          <label className="axa-pickrow" key={u.guid}>
            <input
              type="checkbox"
              checked={chosen.includes(u.guid)}
              onChange={() => toggle(u.guid)}
            />
            <span className="nm">{u.name}</span>
            {alreadyThere[u.guid] ? <span className="there">already there</span> : null}
          </label>
        ))}
        {!listed.length ? <div className="axa-emptyline" style={{ padding: 16 }}>No user matches.</div> : null}
      </div>

      <div className="axa-secthead">
        Destination branch <span className="req">*</span>
      </div>
      <div className="axa-f sel">
        <select value={desBranch} onChange={handleDestination}>
          <option value="">---Select Branch---</option>
          {BranchByUser?.map((obj) => (
            <option value={obj.branch.guid} key={obj.id}>
              {obj?.branch.name}
            </option>
          ))}
        </select>
        <AxCaret />
      </div>

      {skipped ? (
        <div className="axa-warnbox">
          <AxWarnIcon />
          <span>
            {skipped} of them already work in {destName} — they are left out of the transfer.
          </span>
        </div>
      ) : null}

      <div className="axa-preview">
        <div className="h">What will change</div>
        {desBranch && moving.length ? (
          moving.map((u) => (
            <div className="axa-prow2" key={u.guid}>
              <span className="nm">{u.name}</span>
              <span className="from">{branch?.name}</span>
              <span className="arr">→</span>
              <span className="to">{destName}</span>
            </div>
          ))
        ) : (
          <div className="axa-prow2">
            <span className="nm" style={{ color: "var(--ink-3)", fontWeight: 400 }}>
              {desBranch
                ? "Nobody left to move — pick someone who is not already there."
                : "Pick a destination branch to see the effect."}
            </span>
          </div>
        )}
      </div>
    </AxDrawer>
  );
};

export default UserBranchTransferDrawer;
